import { useMemo, useRef, useState } from "react";
import { Minus, Plus, RotateCcw, AlertTriangle, ChevronDown, ListMusic, Share2, Eye, EyeOff, Type } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { metaFor } from "@/lib/sections";
import { transposeChordLine, transposeKey, preferFlatsFor, sanitizeChordText } from "@/lib/chords";

function Line({ line, steps, preferFlat, hideChords, fontPx }) {
  if (line.kind === "annotation") {
    return (
      <div className="my-1" data-testid="cs-annotation">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-sm border border-border bg-background/50 text-muted-foreground">
          {sanitizeChordText(line.text)}
        </span>
      </div>
    );
  }
  const rawCh = sanitizeChordText(line.chords);
  const lyr = sanitizeChordText(line.lyrics);
  const chords = steps ? transposeChordLine(rawCh, steps, preferFlat) : rawCh;
  const fs = { fontSize: `${fontPx}px` };
  return (
    <div className="leading-tight">
      {!hideChords && chords ? <div style={fs} className="font-mono font-bold text-accent whitespace-pre-wrap break-words">{chords}</div> : null}
      {lyr ? <div style={fs} className="font-mono text-foreground whitespace-pre-wrap break-words">{lyr}</div> : null}
    </div>
  );
}

export default function ChordSheetViewer({ structured, music, artist, stage = false, showHeader = true, showNav = true, steps: stepsProp, hideChords: hideChordsProp, fontPx: fontPxProp }) {
  const [stepsI, setStepsI] = useState(0);
  const [hideChordsI, setHideChordsI] = useState(false);
  const [fontPxI, setFontPxI] = useState(15);
  const [expanded, setExpanded] = useState({});
  const [sharing, setSharing] = useState(false);
  const [showFont, setShowFont] = useState(false);
  const refs = useRef({});
  const steps = stepsProp !== undefined ? stepsProp : stepsI;
  const hideChords = hideChordsProp !== undefined ? hideChordsProp : hideChordsI;
  const fontPx = fontPxProp !== undefined ? fontPxProp : fontPxI;
  const sections = structured?.sections || [];
  const origKey = structured?.original_key || "";
  const curKey = useMemo(() => transposeKey(origKey, steps, preferFlatsFor(transposeKey(origKey, steps, false))), [origKey, steps]);
  const preferFlat = preferFlatsFor(curKey);

  const scrollTo = (id) => { const el = refs.current[id]; if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

  const sharePdf = async () => {
    setSharing(true);
    try {
      const res = await api.post("/songs/sheet.pdf", { title: music || "", artist: artist || "", structured }, { responseType: "blob" });
      const fname = `${(music || "cifra").replace(/\s+/g, "_").slice(0, 40) || "cifra"}.pdf`;
      const file = new File([res.data], fname, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: music || "Cifra", text: `Cifra: ${music || ""}${artist ? ` - ${artist}` : ""}` });
      } else {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
        URL.revokeObjectURL(url);
        toast.success("PDF da cifra baixado!");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Não foi possível gerar o PDF.");
    } finally { setSharing(false); }
  };

  if (!sections.length) return <div className="text-muted-foreground text-sm" data-testid="cs-empty">Sem estrutura para exibir.</div>;

  return (
    <div data-testid="chord-sheet-viewer" className="space-y-4">
      {showHeader && (
        <div className="flex flex-wrap items-center justify-between gap-3 sticky top-0 z-10 bg-surface/95 backdrop-blur py-2 -mx-1 px-1 border-b border-border">
          <div className="min-w-0">
            {music ? <div className="font-head font-bold truncate">{music}</div> : null}
            <div className="text-xs text-muted-foreground truncate">
              {artist ? `${artist} · ` : ""}Tom original: <span className="font-mono text-accent">{origKey || "—"}</span>
              {steps !== 0 && <> · Atual: <span className="font-mono text-primary font-semibold">{curKey || "—"}</span></>}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button data-testid="cs-toggle-chords" onClick={() => setHideChordsI((v) => !v)} aria-label={hideChords ? "Mostrar cifras" : "Ocultar cifras (só letra)"} aria-pressed={hideChords} title={hideChords ? "Mostrar cifras" : "Só letra (cantores)"}
              className={`h-9 w-9 grid place-items-center rounded-md border transition-colors ${hideChords ? "bg-accent/15 border-accent/40 text-accent" : "bg-transparent border-border text-muted-foreground hover:text-foreground"}`}>
              {hideChords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <div className="relative">
              <button data-testid="cs-font-size-trigger" onClick={() => setShowFont((v) => !v)} aria-label="Tamanho da fonte" aria-expanded={showFont}
                className="h-9 px-2 inline-flex items-center gap-1 rounded-md border border-border bg-transparent text-muted-foreground hover:text-foreground transition-colors">
                <Type className="h-4 w-4" /><span className="font-mono text-xs">{fontPx}</span>
              </button>
              {showFont && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setShowFont(false)} aria-hidden="true" />
                  <div data-testid="cs-font-size-popover" className="absolute left-0 sm:left-auto sm:right-0 mt-2 z-30 w-60 max-w-[calc(100vw-1.5rem)] rounded-lg border border-border bg-card p-4 shadow-xl space-y-3">
                    <div className="text-xs font-semibold text-muted-foreground">Tamanho da fonte</div>
                    <div className="flex items-center justify-between gap-2">
                      <Button size="icon" variant="outline" data-testid="cs-font-size-dec" aria-label="Diminuir fonte" onClick={() => setFontPxI((f) => Math.max(12, f - 1))} className="h-9 w-9 rounded-md bg-transparent"><Minus className="h-4 w-4" /></Button>
                      <span className="font-mono font-bold text-base" data-testid="cs-font-size-val">{fontPx}px</span>
                      <Button size="icon" variant="outline" data-testid="cs-font-size-inc" aria-label="Aumentar fonte" onClick={() => setFontPxI((f) => Math.min(28, f + 1))} className="h-9 w-9 rounded-md bg-transparent"><Plus className="h-4 w-4" /></Button>
                    </div>
                    <input data-testid="cs-font-size-slider" type="range" min="12" max="28" value={fontPx} onChange={(e) => setFontPxI(parseInt(e.target.value))} className="w-full h-2 accent-accent" aria-label="Ajustar tamanho da fonte" />
                  </div>
                </>
              )}
            </div>
            <Button size="sm" variant="outline" data-testid="cs-share-pdf" onClick={sharePdf} disabled={sharing} className="h-9 rounded-md bg-transparent gap-1.5"><Share2 className="h-4 w-4" />{sharing ? "..." : "PDF"}</Button>
            <div className="mx-0.5 h-6 w-px bg-border" />
            <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider hidden sm:inline">Tom</span>
            <Button size="icon" variant="outline" aria-label="Descer meio tom" data-testid="cs-transpose-down" onClick={() => setStepsI((s) => s - 1)} className="h-9 w-9 rounded-md bg-transparent"><Minus className="h-4 w-4" /></Button>
            <span className="font-mono text-base w-11 text-center font-semibold text-accent" data-testid="cs-current-key">{curKey || "—"}</span>
            <Button size="icon" variant="outline" aria-label="Subir meio tom" data-testid="cs-transpose-up" onClick={() => setStepsI((s) => s + 1)} className="h-9 w-9 rounded-md bg-transparent"><Plus className="h-4 w-4" /></Button>
            {steps !== 0 && stepsProp === undefined && <Button size="icon" variant="ghost" aria-label="Voltar ao tom original" data-testid="cs-transpose-reset" onClick={() => setStepsI(0)} className="h-9 w-9 rounded-md"><RotateCcw className="h-4 w-4" /></Button>}
          </div>
        </div>
      )}

      {showNav && (
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar" data-testid="cs-nav">
          {sections.map((sec) => {
            const meta = metaFor(sec.type); const Icon = meta.icon;
            const num = (sec.title.match(/\d+/) || [""])[0];
            return (
              <button key={sec.id} data-testid={`cs-nav-${sec.id}`} onClick={() => scrollTo(sec.id)}
                className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1.5 rounded-md border border-border bg-surface text-muted-foreground hover:text-accent hover:border-accent/50 transition-colors">
                <Icon className="h-3 w-3" />{meta.short}{num && meta.short === "V" ? num : ""}
              </button>
            );
          })}
        </div>
      )}

      <div className="space-y-3">
        {sections.map((sec) => {
          const meta = metaFor(sec.type); const Icon = meta.icon;
          const low = (sec.confidence ?? 1) < 0.7;
          const isRepeat = !!sec.repeat_of;
          const showBody = !isRepeat || expanded[sec.id];
          const num = (sec.title.match(/\d+/) || [""])[0];
          const label = meta.short === "V" ? `Verso ${num}`.trim() : sec.title;
          return (
            <section key={sec.id} ref={(el) => (refs.current[sec.id] = el)} data-testid={`cs-section-${sec.id}`}
              className="rounded-lg border border-border overflow-hidden">
              {sec.song_title ? (
                <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-accent bg-surface border-b border-border/50" data-testid={`cs-subsong-${sec.id}`}>
                  <ListMusic className="h-4 w-4 shrink-0" /><span className="truncate">{sec.song_title}{sec.song_artist ? ` · ${sec.song_artist}` : ""}</span>
                  {sec.key && <span className="ml-auto font-mono text-muted-foreground shrink-0">Tom {transposeKey(sec.key, steps, preferFlat)}</span>}
                </div>
              ) : null}
              <header className="relative flex items-center justify-center gap-2 px-3 py-2 bg-accent text-accent-foreground">
                <Icon className="h-4 w-4 shrink-0 opacity-80" />
                <span className="font-head font-bold text-sm uppercase tracking-wide truncate">{label}</span>
                {sec.repeat > 1 && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-accent-foreground/15">×{sec.repeat}</span>}
                {sec.key && !sec.song_title && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-accent-foreground/15">Tom {transposeKey(sec.key, steps, preferFlat)}</span>}
                {low && <AlertTriangle className="h-3.5 w-3.5 shrink-0" title="Confiança baixa — verifique" data-testid={`cs-warn-${sec.id}`} />}
                {isRepeat && (
                  <button data-testid={`cs-expand-${sec.id}`} onClick={() => setExpanded((e) => ({ ...e, [sec.id]: !e[sec.id] }))} className="absolute right-2 text-[11px] inline-flex items-center gap-1 hover:opacity-80">
                    <ChevronDown className={`h-3 w-3 transition-transform ${expanded[sec.id] ? "rotate-180" : ""}`} />
                  </button>
                )}
              </header>
              {showBody ? (
                <div className="px-3 py-2.5 space-y-1 bg-surface">
                  {sec.lines.map((l, i) => <Line key={i} line={l} steps={steps} preferFlat={preferFlat} hideChords={hideChords} fontPx={fontPx} />)}
                </div>
              ) : (
                <div className="px-3 py-2 text-xs text-muted-foreground bg-surface">Repetição — toque no botão da seção para expandir.</div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
